// Phase 15 Plan 05 Task 2 — $friendsList store (Plan 07 friends-tab consumer).
//
// `null` = not yet loaded; `FriendsListResponse` = the server's payload.
// Refresh on:
//   - explicit initial load (Plan 07's friends-tab mount calls refreshFriendsList)
//   - `runegambit:friend-request-accepted` DOM event (cross-tree signal —
//     dispatched by Plan 07's accept-button handler on a successful 200)
//
// Plain `atom()` — in-memory only, no localStorage.

import { atom } from 'nanostores'
import { type FriendsListResponse, fetchFriendsList } from '../net/friends'

export const $friendsList = atom<FriendsListResponse | null>(null)

export function setFriendsList(list: FriendsListResponse | null): void {
  $friendsList.set(list)
}

export function clearFriendsList(): void {
  $friendsList.set(null)
}

/**
 * Fetch /api/friends + write the result into $friendsList. Errors are
 * swallowed (the prior value stays put); apiFetch already handles 5xx/401.
 */
export async function refreshFriendsList(): Promise<void> {
  try {
    const data = await fetchFriendsList()
    $friendsList.set(data)
  } catch {
    // Keep prior value to avoid blanking the UI on a transient blip.
  }
}

/**
 * Wire the `runegambit:friend-request-accepted` cross-tree DOM event so
 * accepting an inbound request immediately surfaces the new friend in the
 * friends list. Returns a teardown. Plan 07's AcceptButton dispatches this
 * event on a 200 response from POST /api/friends/accept.
 */
export function initFriendsListListeners(): () => void {
  const onAccepted = (): void => {
    void refreshFriendsList()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('runegambit:friend-request-accepted', onAccepted)
  }
  return (): void => {
    if (typeof window !== 'undefined') {
      window.removeEventListener(
        'runegambit:friend-request-accepted',
        onAccepted,
      )
    }
  }
}
